import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Sparkles, AlertTriangle, Palette, FileSignature, Mail, Send, CheckCircle2, XCircle } from "lucide-react";
import { Logo, LoadingMark } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [emails, setEmails] = useState(null);
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailNotice, setEmailNotice] = useState(null);
  const [digestBusy, setDigestBusy] = useState(false);
  const [traffic, setTraffic] = useState(null);
  const [testimonials, setTestimonials] = useState(null);
  const [testimonialBusy, setTestimonialBusy] = useState(null);
  const [researchStatus, setResearchStatus] = useState(null);
  const [reports, setReports] = useState(null);
  const [researchQuery, setResearchQuery] = useState("");
  const [researchBusy, setResearchBusy] = useState(false);
  const [researchError, setResearchError] = useState(null);
  const [openReport, setOpenReport] = useState(null);

  const loadTestimonials = () => {
    const token = localStorage.getItem("doclyx_token");
    axios
      .get(`${API}/admin/testimonials`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true })
      .then((res) => setTestimonials(res.data))
      .catch(() => {});
  };

  const setTestimonialStatus = async (id, status) => {
    setTestimonialBusy(id);
    try {
      const token = localStorage.getItem("doclyx_token");
      await axios.post(`${API}/admin/testimonials/${id}/status`, { status }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      loadTestimonials();
    } finally {
      setTestimonialBusy(null);
    }
  };

  const loadResearch = () => {
    const token = localStorage.getItem("doclyx_token");
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/admin/research/status`, { headers, withCredentials: true }).then((res) => setResearchStatus(res.data)).catch(() => {});
    axios.get(`${API}/admin/research`, { headers, withCredentials: true }).then((res) => setReports(res.data.reports)).catch(() => {});
  };

  const runResearch = async () => {
    if (researchQuery.trim().length < 3) return;
    setResearchBusy(true);
    setResearchError(null);
    try {
      const token = localStorage.getItem("doclyx_token");
      const res = await axios.post(
        `${API}/admin/research`,
        { query: researchQuery.trim(), model: "sonar" },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true },
      );
      setResearchQuery("");
      setOpenReport(res.data.id);
      loadResearch();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setResearchError(detail === "not_configured"
        ? "Research is not configured yet — add PERPLEXITY_API_KEY to the backend environment."
        : detail || "Research failed — try again in a minute.");
    } finally {
      setResearchBusy(false);
    }
  };

  const loadEmails = () => {
    const token = localStorage.getItem("doclyx_token");
    axios
      .get(`${API}/admin/emails`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true })
      .then((res) => setEmails(res.data))
      .catch(() => {});
  };

  const sendTestEmail = async () => {
    setEmailBusy(true);
    setEmailNotice(null);
    try {
      const token = localStorage.getItem("doclyx_token");
      const res = await axios.post(`${API}/admin/emails/test`, {}, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      setEmailNotice({ ok: true, text: `Test email sent to ${res.data.to}` });
      loadEmails();
    } catch (e) {
      setEmailNotice({ ok: false, text: e.response?.data?.detail || "Test email failed" });
    } finally {
      setEmailBusy(false);
    }
  };

  useEffect(() => {
    if (user === false) navigate("/login");
    else if (user && user.role !== "admin") navigate("/account");
  }, [user, navigate]);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    const token = localStorage.getItem("doclyx_token");
    const load = () =>
      axios
        .get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true })
        .then((res) => {
          setStats(res.data);
          setUpdatedAt(new Date());
        })
        .catch(() => setError("Could not load stats."));
    load();
    loadEmails();
    loadTestimonials();
    loadResearch();
    axios
      .get(`${API}/admin/traffic`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true })
      .then((res) => setTraffic(res.data))
      .catch(() => {});
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [user]);

  if (!user || user.role !== "admin") {
    return (
      <main className="bg-page min-h-screen flex items-center justify-center">
        <LoadingMark label="Loading command center" />
      </main>
    );
  }

  const live = stats?.stripe_live || {};
  const dash = (v) => (v === null || v === undefined ? "—" : v);
  const revenue = live.revenue_charged !== undefined ? Number(live.revenue_charged) : Number(stats?.revenue || 0);
  const paidSubs = Number(stats?.paid_subscriptions || 0);
  const paidCustomers = Number(stats?.paid_customers || 0);
  const users = Number(stats?.users || 0);
  const runs = Number(stats?.tool_runs || 0);
  const failures = Number(stats?.tool_failures || 0);
  const ownerRoi = stats
    ? [
        { label: "Revenue / paid sub", value: paidSubs ? `$${(revenue / paidSubs).toFixed(2)}` : "—", testid: "roi-revenue-per-sub" },
        { label: "Paid conversion", value: users ? `${Math.min(100, Math.round((paidCustomers / users) * 100))}%` : "—", testid: "roi-paid-conversion" },
        { label: "Tool success", value: runs ? `${Math.round(((runs - failures) / runs) * 100)}%` : "—", testid: "roi-tool-success" },
        { label: "Revenue / run", value: runs ? `$${(revenue / runs).toFixed(2)}` : "—", testid: "roi-revenue-per-run" },
      ]
    : [];
  const funnelCounts = stats?.funnel?.counts || {};
  const funnelPlans = stats?.funnel?.plans || {};
  const funnelCampaigns = stats?.funnel?.campaigns || {};
  const funnelCampaignRows = Object.entries(funnelCampaigns)
    .sort((a, b) => ((b[1].home_view || 0) * 100 + (b[1].checkout_success || 0)) - ((a[1].home_view || 0) * 100 + (a[1].checkout_success || 0)))
    .slice(0, 8);
  const pct = (part, whole) => (whole ? `${Math.min(100, Math.round((part / whole) * 100))}%` : "—");
  const funnelCards = stats
    ? [
        { label: "Homepage visits", value: funnelCounts.home_view || 0, rate: "entry", testid: "funnel-home" },
        { label: "CTA clicks", value: funnelCounts.cta_click || 0, rate: pct(funnelCounts.cta_click || 0, funnelCounts.home_view || 0), testid: "funnel-ctas" },
        { label: "Pricing views", value: funnelCounts.pricing_view || 0, rate: pct(funnelCounts.pricing_view || 0, funnelCounts.home_view || 0), testid: "funnel-pricing" },
        { label: "Checkout starts", value: funnelCounts.checkout_start || 0, rate: pct(funnelCounts.checkout_start || 0, funnelCounts.pricing_view || 0), testid: "funnel-checkout" },
        { label: "Paid plans", value: funnelCounts.checkout_success || 0, rate: "payments ledger", testid: "funnel-paid" },
      ]
    : [];
  const cards = stats
    ? [
        { label: "Revenue charged", value: live.revenue_charged !== undefined ? `$${Number(live.revenue_charged).toLocaleString()}` : "—", testid: "stat-revenue" },
        { label: "MRR (active)", value: live.mrr !== undefined ? `$${Number(live.mrr).toLocaleString()}` : "—", testid: "stat-mrr" },
        { label: "Active subscriptions", value: dash(live.subs_active), testid: "stat-subscriptions" },
        { label: "On free trial", value: dash(live.subs_trialing), testid: "stat-trialing" },
        { label: "Registered users", value: stats.users, testid: "stat-users" },
        { label: "Email signups", value: stats.subscribers, testid: "stat-subscribers" },
        { label: "Tool runs", value: stats.tool_runs, testid: "stat-tool-runs" },
        { label: "Tool failures", value: stats.tool_failures, testid: "stat-failures", danger: stats.tool_failures > 0 },
        { label: "Wingman chats", value: stats.wingman_chats, testid: "stat-chats" },
      ]
    : [];

  return (
    <main className="bg-page text-ink min-h-screen px-5 py-8 md:px-6 md:py-10" data-testid="admin-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10 md:mb-14">
          <Link to="/account" data-testid="admin-home-link"><Logo /></Link>
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-fog" data-testid="admin-live-indicator">
              <span className="w-1.5 h-1.5 rounded-full bg-azure animate-blink" />
              Live{updatedAt ? ` · ${updatedAt.toLocaleTimeString()}` : ""}
            </span>
            <Link to="/admin/brand" data-testid="admin-brand-link"
              className="flex items-center gap-2 text-sm text-fog hover:text-ink transition-colors duration-300">
              <Palette className="w-4 h-4" strokeWidth={1.5} /> Brand kit
            </Link>
            <Link to="/admin/legal" data-testid="admin-legal-link"
              className="flex items-center gap-2 text-sm text-fog hover:text-ink transition-colors duration-300">
              <FileSignature className="w-4 h-4" strokeWidth={1.5} /> Legal
            </Link>
            <Link to="/admin/wingman" data-testid="admin-studio-link"
              className="flex items-center gap-2 text-sm text-fog hover:text-ink transition-colors duration-300">
              <Sparkles className="w-4 h-4" strokeWidth={1.5} /> Marketing studio
            </Link>
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}>
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-mist block mb-4">Command center</span>
          <h1 className="font-medium tracking-tight text-3xl md:text-4xl mb-12" data-testid="admin-title">How Doclyx is doing.</h1>

          {error && <p className="text-xs text-[#FF3333] mb-6" data-testid="admin-error">{error}</p>}
          {!stats && !error && <Loader2 className="w-6 h-6 text-mist animate-spin" strokeWidth={1.5} />}

          {stats && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-12" data-testid="admin-stats-grid">
                {cards.map((c) => (
                  <div key={c.label} className="bg-white border border-edge rounded-sm p-6" data-testid={c.testid}>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist block mb-3">{c.label}</span>
                    <span className={`text-3xl font-medium tracking-tight ${c.danger ? "text-[#FF3333]" : ""}`}>{c.value}</span>
                  </div>
                ))}
              </div>

              <div className="bg-ink text-white rounded-sm p-6 md:p-7 mb-12" data-testid="owner-roi-panel">
                <div className="flex items-center justify-between gap-4 mb-5">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Owner ROI</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Admin only</span>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                  {ownerRoi.map((m) => (
                    <div key={m.label} data-testid={m.testid}>
                      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/45 block mb-2">{m.label}</span>
                      <span className="text-2xl font-medium tracking-tight">{m.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white border border-edge rounded-sm p-6 md:p-7 mb-12" data-testid="conversion-funnel-panel">
                <div className="flex items-center justify-between gap-4 mb-5">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist">Conversion funnel</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist">Homepage → paid</span>
                </div>
                <p className="font-mono text-[10px] text-mist mb-5">Top-of-funnel events are browser-tracked; Paid plans are reconciled from completed payment records.</p>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                  {funnelCards.map((m) => (
                    <div key={m.label} className="border border-hairline rounded-lg p-4" data-testid={m.testid}>
                      <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-mist block mb-2">{m.label}</span>
                      <span className="text-2xl font-medium tracking-tight block">{m.value}</span>
                      <span className="font-mono text-[9px] text-fog">{m.rate}</span>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5" data-testid="funnel-plan-mix">
                  {Object.entries(funnelPlans).map(([plan, counts]) => (
                    <div key={plan} className="flex items-center justify-between border border-hairline rounded-lg px-4 py-3 font-mono text-xs" data-testid={`funnel-plan-${plan}`}>
                      <span className="text-ink">{plan.replace("_monthly", "")}</span>
                      <span className="text-fog">{counts.checkout_start} starts · {counts.checkout_success} paid</span>
                    </div>
                  ))}
                  {Object.keys(funnelPlans).length === 0 && <p className="text-sm text-fog">No checkout activity yet.</p>}
                </div>
                <div className="border-t border-hairline pt-5" data-testid="funnel-campaigns">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist block mb-3">Campaign performance</span>
                  <div className="space-y-2">
                    {funnelCampaignRows.map(([campaignName, counts]) => (
                      <div key={campaignName} className="flex items-center justify-between font-mono text-xs" data-testid={`funnel-campaign-${campaignName}`}>
                        <span className="text-ink">{campaignName}</span>
                        <span className="text-fog">{counts.home_view || 0} visits · {counts.checkout_start || 0} starts · {counts.checkout_success || 0} paid</span>
                      </div>
                    ))}
                    {Object.keys(funnelCampaigns).length === 0 && <p className="text-sm text-fog">No UTM campaign traffic yet.</p>}
                  </div>
                </div>
              </div>

              <div className="bg-white border border-edge rounded-sm overflow-hidden" data-testid="admin-traffic">
                <div className="px-6 py-4 border-b border-hairline flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist">Traffic & funnel — last 30 days</span>
                  {traffic && <span className="font-mono text-[10px] text-mist">live</span>}
                </div>
                {!traffic ? (
                  <p className="px-6 py-6 text-sm text-fog">Loading traffic…</p>
                ) : (
                  <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div data-testid="traffic-funnel">
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist block mb-3">Funnel</span>
                      <div className="space-y-2.5">
                        {[
                          { label: "Page views", value: traffic.funnel.views },
                          { label: "Free conversions", value: traffic.funnel.guest_runs },
                          { label: "New accounts", value: traffic.funnel.signups },
                          { label: "Paid", value: traffic.funnel.paid },
                        ].map((step, i) => {
                          const max = traffic.funnel.views || 1;
                          const pct = Math.max(4, Math.round((step.value / max) * 100));
                          return (
                            <div key={step.label} data-testid={`traffic-funnel-${i}`}>
                              <div className="flex items-center justify-between font-mono text-[11px] mb-1">
                                <span className="text-fog">{step.label}</span>
                                <span className="text-ink tabular-nums">{step.value.toLocaleString()}</span>
                              </div>
                              <div className="h-1.5 rounded-full bg-page overflow-hidden">
                                <div className="h-full rounded-full bg-azure transition-all duration-700" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {traffic.utm.length > 0 && (
                        <div className="mt-5" data-testid="traffic-utm">
                          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist block mb-2">Top sources</span>
                          <div className="flex flex-wrap gap-1.5">
                            {traffic.utm.map((u) => (
                              <span key={u.source} className="font-mono text-[10px] border border-hairline rounded-full px-2.5 py-1 text-fog" data-testid={`traffic-utm-${u.source}`}>
                                {u.source} · {u.hits}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div data-testid="traffic-pages">
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist block mb-3">Top pages</span>
                      {traffic.pages.length === 0 ? (
                        <p className="text-sm text-fog" data-testid="traffic-pages-empty">No page views recorded yet — tracking starts the moment V7 ships.</p>
                      ) : (
                        <div className="space-y-2">
                          {traffic.pages.slice(0, 8).map((p, i) => {
                            const max = traffic.pages[0].views || 1;
                            return (
                              <div key={p.path || i} className="flex items-center gap-3" data-testid={`traffic-page-${i}`}>
                                <span className="font-mono text-[11px] text-ink truncate w-44">/{p.path}</span>
                                <div className="flex-1 h-1.5 rounded-full bg-page overflow-hidden">
                                  <div className="h-full rounded-full bg-ink/70" style={{ width: `${Math.max(4, Math.round((p.views / max) * 100))}%` }} />
                                </div>
                                <span className="font-mono text-[11px] text-fog tabular-nums w-14 text-right">{p.views}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div data-testid="traffic-daily">
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist block mb-3">Daily views — 14 days</span>
                      <div className="flex items-end gap-1 h-28">
                        {traffic.daily.map((d, i) => {
                          const max = Math.max(...traffic.daily.map((x) => x.views), 1);
                          return (
                            <div
                              key={d.date}
                              title={`${d.date}: ${d.views} views`}
                              data-testid={`traffic-day-${i}`}
                              className="flex-1 rounded-sm bg-azure/80 transition-all duration-500"
                              style={{ height: `${Math.max(3, Math.round((d.views / max) * 100))}%` }}
                            />
                          );
                        })}
                      </div>
                      <p className="font-mono text-[10px] text-mist mt-2 flex justify-between">
                        <span>{traffic.daily[0]?.date.slice(5)}</span>
                        <span>{traffic.daily[13]?.date.slice(5)}</span>
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-white border border-edge rounded-sm overflow-hidden" data-testid="admin-tool-health">
                  <div className="px-6 py-4 border-b border-hairline flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-mist" strokeWidth={1.5} />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist">Tool health — recent failures</span>
                  </div>
                  <div className="divide-y divide-hairline">
                    {stats.recent_failures.length === 0 && (
                      <p className="px-6 py-6 text-sm text-fog" data-testid="admin-no-failures">All systems clean — no failed runs.</p>
                    )}
                    {stats.recent_failures.map((f, i) => (
                      <div key={i} className="px-6 py-3.5 font-mono text-xs" data-testid={`admin-failure-${i}`}>
                        <span className="text-[#FF3333]">{f.action}</span>
                        <span className="text-fog"> — {f.error}</span>
                        <span className="text-mist block mt-1">{new Date(f.at).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white border border-edge rounded-sm overflow-hidden" data-testid="admin-payments">
                  <div className="px-6 py-4 border-b border-hairline">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist">Recent payments</span>
                  </div>
                  <div className="divide-y divide-hairline">
                    {stats.recent_payments.length === 0 && (
                      <p className="px-6 py-6 text-sm text-fog">No payments yet.</p>
                    )}
                    {stats.recent_payments.map((p, i) => (
                      <div key={i} className="px-6 py-3.5 flex items-center justify-between font-mono text-xs" data-testid={`admin-payment-${i}`}>
                        <span className="text-ink">{p.lookup_key?.replace("_monthly", "")}</span>
                        <span className="text-fog">${p.amount}</span>
                        <span className="text-mist">{p.updated_at ? new Date(p.updated_at).toLocaleDateString() : ""}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white border border-edge rounded-sm overflow-hidden" data-testid="admin-email-pipeline">
                  <div className="px-6 py-4 border-b border-hairline flex items-center justify-between gap-3">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist flex items-center gap-2">
                      <Mail className="w-4 h-4" strokeWidth={1.5} /> Email pipeline — Resend
                    </span>
                    {emails && (
                      <span className="font-mono text-[10px] uppercase tracking-[0.15em]" data-testid="email-pipeline-counts">
                        <span className="text-azure">{emails.total} sent</span>
                        {emails.failed > 0 && <span className="text-[#FF3333]"> · {emails.failed} failed</span>}
                      </span>
                    )}
                  </div>
                  <div className="px-6 py-4 border-b border-hairline">
                    <button
                      type="button"
                      onClick={sendTestEmail}
                      disabled={emailBusy}
                      data-testid="email-test-button"
                      className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-azure hover:text-ink disabled:opacity-40 transition-colors duration-300"
                    >
                      {emailBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> : <Send className="w-3.5 h-3.5" strokeWidth={1.5} />}
                      Send test email
                    </button>
                    {emailNotice && (
                      <p className={`font-mono text-[10px] mt-2 ${emailNotice.ok ? "text-azure" : "text-[#FF3333]"}`} data-testid="email-test-notice">
                        {emailNotice.text}
                      </p>
                    )}
                  </div>
                  <div className="divide-y divide-hairline max-h-72 overflow-y-auto">
                    {!emails && <p className="px-6 py-6 text-sm text-fog">Loading…</p>}
                    {emails && emails.emails.length === 0 && (
                      <p className="px-6 py-6 text-sm text-fog" data-testid="email-log-empty">No emails logged yet — send a test above.</p>
                    )}
                    {emails?.emails.map((e, i) => (
                      <div key={i} className="px-6 py-3 flex items-start gap-2.5 font-mono text-xs" data-testid={`email-row-${i}`}>
                        {e.ok ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-azure shrink-0 mt-0.5" strokeWidth={1.5} />
                        ) : (
                          <XCircle className="w-3.5 h-3.5 text-[#FF3333] shrink-0 mt-0.5" strokeWidth={1.5} />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-ink truncate">{e.subject}</p>
                          <p className="text-fog truncate">to {e.to}</p>
                          <p className="text-mist mt-0.5">{new Date(e.at).toLocaleString()}{e.error ? ` · ${e.error}` : ""}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="px-6 py-4 border-t border-hairline flex items-center justify-between">
                    <p className="text-[11px] text-mist">Weekly digest: Mondays 09:00 UTC, automatically.</p>
                    <button
                      type="button"
                      data-testid="digest-send-button"
                      disabled={digestBusy}
                      onClick={async () => {
                        setDigestBusy(true);
                        try {
                          const token = localStorage.getItem("doclyx_token");
                          const res = await axios.post(`${API}/admin/digest/send`, {}, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
                          const s = res.data.stats;
                          setEmailNotice({ ok: true, text: `Digest sent — $${s.revenue.toFixed(2)} revenue, ${s.new_users} new accounts, ${s.tool_runs} conversions this week.` });
                        } catch (e2) {
                          setEmailNotice({ ok: false, text: e2.response?.data?.detail || "Digest could not be sent" });
                        } finally {
                          setDigestBusy(false);
                        }
                      }}
                      className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.12em] text-fog hover:text-azure transition-colors duration-300 disabled:opacity-50"
                    >
                      {digestBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" strokeWidth={1.5} />}
                      Send weekly digest now
                    </button>
                  </div>
                </div>
                <div className="bg-white border border-edge rounded-sm overflow-hidden" data-testid="admin-testimonials">
                  <div className="px-6 py-4 border-b border-hairline flex items-center justify-between gap-3">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist">Testimonials</span>
                    {testimonials && (
                      <span className="font-mono text-[10px] uppercase tracking-[0.15em]" data-testid="testimonials-counts">
                        <span className="text-bronze">{testimonials.pending} pending</span>
                        <span className="text-mist"> · {testimonials.testimonials.filter((t) => t.status === "approved").length} live</span>
                      </span>
                    )}
                  </div>
                  <div className="divide-y divide-hairline max-h-80 overflow-y-auto">
                    {!testimonials && <p className="px-6 py-6 text-sm text-fog">Loading…</p>}
                    {testimonials && testimonials.testimonials.length === 0 && (
                      <p className="px-6 py-6 text-sm text-fog" data-testid="testimonials-empty">
                        No feedback yet — it appears here after someone runs a file and shares a line.
                      </p>
                    )}
                    {testimonials?.testimonials.map((t) => (
                      <div key={t.id} className="px-6 py-4" data-testid={`testimonial-row-${t.id}`}>
                        <p className="text-sm text-ink leading-relaxed mb-1.5">&ldquo;{t.quote}&rdquo;</p>
                        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-mist mb-3">
                          {t.name}{t.role ? ` · ${t.role}` : ""} · {new Date(t.created_at).toLocaleDateString()}
                        </p>
                        <div className="flex items-center gap-2">
                          {t.status !== "approved" ? (
                            <button
                              type="button"
                              onClick={() => setTestimonialStatus(t.id, "approved")}
                              disabled={testimonialBusy === t.id}
                              data-testid={`testimonial-approve-${t.id}`}
                              className="inline-flex items-center min-h-[44px] border border-hairline rounded-full px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-azure hover:border-ink hover:text-ink disabled:opacity-40 transition-colors duration-300"
                            >
                              Approve
                            </button>
                          ) : (
                            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-azure">Live on site</span>
                          )}
                          {t.status !== "rejected" && (
                            <button
                              type="button"
                              onClick={() => setTestimonialStatus(t.id, "rejected")}
                              disabled={testimonialBusy === t.id}
                              data-testid={`testimonial-reject-${t.id}`}
                              className="inline-flex items-center min-h-[44px] border border-hairline rounded-full px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-mist hover:border-[#FF3333]/40 hover:text-[#FF3333] disabled:opacity-40 transition-colors duration-300"
                            >
                              Reject
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white border border-edge rounded-sm overflow-hidden" data-testid="admin-research">
                  <div className="px-6 py-4 border-b border-hairline flex items-center justify-between gap-3">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist flex items-center gap-2">
                      <Sparkles className="w-4 h-4" strokeWidth={1.5} /> Market research — Perplexity
                    </span>
                    {researchStatus && (
                      <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-mist" data-testid="research-status">
                        {researchStatus.configured ? `${researchStatus.used_today}/${researchStatus.daily_limit} today` : "Not configured"}
                      </span>
                    )}
                  </div>
                  <div className="p-6 space-y-4">
                    {researchStatus && !researchStatus.configured && (
                      <div className="border border-hairline rounded-lg bg-page p-4" data-testid="research-setup">
                        <p className="text-sm text-ink font-medium mb-1">One step to switch this on</p>
                        <p className="text-[13px] text-fog leading-relaxed">
                          Get an API key at console.perplexity.ai (project → API keys → generate), then add
                          <span className="font-mono text-xs text-ink"> PERPLEXITY_API_KEY</span> to the backend environment and restart.
                          Keys start with pplx- and are shown only once.
                        </p>
                      </div>
                    )}
                    <textarea
                      value={researchQuery}
                      onChange={(e) => setResearchQuery(e.target.value)}
                      rows={2}
                      maxLength={2000}
                      placeholder="e.g. What PDF tasks are legal teams searching for most in 2026, and where do Smallpdf and iLovePDF leave gaps?"
                      data-testid="research-query-input"
                      className="w-full border border-hairline rounded-lg bg-page px-3.5 py-2.5 font-mono text-xs text-ink placeholder:text-mist focus:outline-none focus:border-azure transition-colors duration-300 resize-none"
                    />
                    {researchError && <p className="font-mono text-[10px] text-[#FF3333]" data-testid="research-error">{researchError}</p>}
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={runResearch}
                        disabled={researchBusy || researchQuery.trim().length < 3 || (researchStatus && !researchStatus.configured)}
                        data-testid="research-run-button"
                        className="inline-flex items-center gap-2 v4-btn-primary rounded-full px-5 min-h-[44px] font-mono text-[10px] uppercase tracking-[0.15em] disabled:opacity-40 transition-colors duration-300"
                      >
                        {researchBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> : null}
                        Run research
                      </button>
                    </div>
                    {reports && reports.length > 0 && (
                      <div className="divide-y divide-hairline border-t border-hairline" data-testid="research-reports">
                        {reports.map((r) => (
                          <div key={r.id}>
                            <button
                              type="button"
                              onClick={() => setOpenReport(openReport === r.id ? null : r.id)}
                              data-testid={`research-report-${r.id}`}
                              className="w-full text-left px-1 py-3.5 min-h-[44px] flex items-center justify-between gap-3"
                            >
                              <span className="text-sm text-ink truncate">{r.query}</span>
                              <span className="font-mono text-[10px] text-mist shrink-0">{new Date(r.created_at).toLocaleDateString()}</span>
                            </button>
                            {openReport === r.id && (
                              <div className="pb-5 px-1" data-testid={`research-answer-${r.id}`}>
                                <p className="text-[13px] text-fog leading-relaxed whitespace-pre-wrap mb-3">{r.answer}</p>
                                {r.sources.length > 0 && (
                                  <div className="space-y-1">
                                    {r.sources.map((s, i) => (
                                      <a key={`${s.url}-${i}`} href={s.url} target="_blank" rel="noreferrer"
                                        className="block font-mono text-[11px] text-azure hover:text-ink truncate transition-colors duration-300"
                                        data-testid={`research-source-${r.id}-${i}`}>
                                        {s.title || s.url}{s.date ? ` · ${s.date}` : ""}
                                      </a>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </main>
  );
}
